// src/StructuralPage.js
import React, { useState } from 'react';
import { dedupeChecklist } from './utils/dedupeChecklist';

export default function StructuralPage({ engineChecklist, bodyChecklist, images }) {
  const colorBlue = '#153BC6';
  const lightGray1 = '#f7f7f7';
  const lightGray2 = '#eaeaea';

  // Scroll helpers for engine/body sections
  const scrollToEngineDamage = () => {
    const el =
      document.getElementById('damage-structuraldamageengine') ||
      document.getElementById('damage-report');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToBodyDamage = () => {
    const el =
      document.getElementById('damage-structuraldamagebody') ||
      document.getElementById('damage-report');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Modal image state
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  /* -------- render helpers (icon & images) --------------- */
  function renderDamageIcon(severity) {
    const baseStyle = {
      display: 'inline-block',
      verticalAlign: 'middle',
    };

    if (severity === 'Light') {
      // use the MagnifyingGlass.png from public/
      return (
        <img
          src="/MagnifyingGlass.png"
          alt="Inspect"
          style={{
            ...baseStyle,
            width: '1.4rem',
            height: '1.4rem',
          }}
        />
      );
    }

    if (severity === 'Moderate') {
      return <span style={{ ...baseStyle, color: 'orange', fontSize: '1.4rem' }}>&#9888;</span>;
    }
    if (severity === 'Severe') {
      return <span style={{ ...baseStyle, color: 'red', fontSize: '1.8rem' }}>&#10007;</span>;
    }
    return <span style={{ ...baseStyle, color: 'green', fontSize: '1.4rem' }}>&#10003;</span>;
  }

  // Image keys and URL array
  const structuralImageKeys = [
    'LeftFrontRockerImage',
    'LeftRearRockerImage',
    'RightFrontRockerImage',
    'RightRearRockerImage',
    'FrontFrameImage',
    'LeftFrontFrameImage',
    'AdditionalFrame1Image',
    'LeftRearFrameImage',
    'AdditionalFrame2Image',
    'RearFrameImage',
    'RightRearFrameImage',
    'AdditionalFrame3Image',
    'RightFrontFrameImage',
    'AdditionalFrame4Image',
    'LeftFrontSuspensionImage',
    'LeftRearSuspensionImage',
    'RightFrontSuspensionImage',
    'RightRearSuspensionImage',
    'LeftFrameRailImage',
    'RightFrameRailImage',
  ];
  const structuralUrls = structuralImageKeys.map((k) => images[k]).filter(Boolean);

  // Modal handlers
  function openModal(idx) {
    setSelectedIndex(idx);
    setZoomed(false);
  }
  function closeModal() {
    setSelectedIndex(null);
    setZoomed(false);
  }
  function toggleZoom() {
    setZoomed((z) => !z);
  }
  function showPrev(e) {
    e.stopPropagation();
    if (selectedIndex > 0) {
      setSelectedIndex((i) => i - 1);
      setZoomed(false);
    }
  }
  function showNext(e) {
    e.stopPropagation();
    if (selectedIndex < structuralUrls.length - 1) {
      setSelectedIndex((i) => i + 1);
      setZoomed(false);
    }
  }

  return (
    <div
      className="report-page"
      style={{
        width: '8.5in',
        minHeight: '11in',
        margin: '0 auto',
        padding: '0.3in',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
        fontFamily: 'Arial, sans-serif',
        position: 'relative',
      }}
    >
      <h2 style={{ marginBottom: '0.2in', color: colorBlue }}>Structural Inspection</h2>

      <div style={{ display: 'flex', gap: '10px' }}>
        {/* Engine & Body Checklists */}
        <div style={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Engine Checklist */}
          {engineChecklist.length > 0 && (
            <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
              <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
                Structural (Engine) Checklist
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {dedupeChecklist(engineChecklist).map((item, idx) => {
                    const bg = idx % 2 === 0 ? lightGray1 : lightGray2;
                    const clickable = ['Light', 'Moderate', 'Severe'].includes(item.DamageSeverity);
                    return (
                      <tr key={item.DropdownValueId} style={{ backgroundColor: bg }}>
                        <td style={{ padding: '4px 8px', verticalAlign: 'middle' }}>
                          {item.DropdownValue}
                        </td>
                        <td
                          style={{
                            padding: '4px 8px',
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            cursor: clickable ? 'pointer' : 'default',
                          }}
                          onClick={clickable ? scrollToEngineDamage : undefined}
                          title={clickable ? 'Jump to Damage Report' : undefined}
                        >
                          {renderDamageIcon(item.DamageSeverity)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Body Checklist */}
          {bodyChecklist.length > 0 && (
            <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
              <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
                Structural (Body) Checklist
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {dedupeChecklist(bodyChecklist).map((item, idx) => {
                    const bg = idx % 2 === 0 ? lightGray1 : lightGray2;
                    const clickable = ['Light', 'Moderate', 'Severe'].includes(item.DamageSeverity);
                    return (
                      <tr key={item.DropdownValueId} style={{ backgroundColor: bg }}>
                        <td style={{ padding: '4px 8px', verticalAlign: 'middle' }}>
                          {item.DropdownValue}
                        </td>
                        <td
                          style={{
                            padding: '4px 8px',
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            cursor: clickable ? 'pointer' : 'default',
                          }}
                          onClick={clickable ? scrollToBodyDamage : undefined}
                          title={clickable ? 'Jump to Damage Report' : undefined}
                        >
                          {renderDamageIcon(item.DamageSeverity)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Structural Images & Notes */}
        <div style={{ flex: '0 0 50%' }}>
          {/* Images */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              marginBottom: '0.2in',
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Structural Images
            </h3>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                rowGap: '4px',
                columnGap: '8px',
                alignItems: 'flex-start',
                alignContent: 'flex-start',
              }}
            >
              {structuralImageKeys.map((key) => {
                const url = images[key];
                if (!url) return null;
                const idx = structuralUrls.indexOf(url);
                return (
                  <div
                    key={key}
                    style={{
                      width: '80px',
                      height: '80px',
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                    onClick={() => openModal(idx)}
                  >
                    <img
                      src={url}
                      alt={key}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              backgroundColor: '#f9f9f9',
              padding: '10px',
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 6, fontSize: '1rem' }}>
              Important Structural Notes
            </h3>
            <p style={{ margin: '6px 0' }}>
              At RevCheck, structural damage is never taken lightly. We inspect key structural areas
              following NAAA industry guidelines and report any damage we find—whether it’s been repaired or
              not.
            </p>
            <p style={{ margin: '6px 0' }}>
              Structural damage is a potential safety concern, and we do not use a severity scale. If it’s
              there, we call it.
            </p>
            <p style={{ margin: '6px 0' }}>
              While we document visible signs, we cannot confirm the quality or safety of any past repairs.
              We strongly recommend having any structural findings reviewed by a qualified body shop
              before moving forward with a purchase.
            </p>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div style={{ position: 'absolute', bottom: 10, right: 10 }}>
        <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
      </div>

      {/* Image Modal */}
      {selectedIndex !== null && structuralUrls[selectedIndex] && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <img
            src={structuralUrls[selectedIndex]}
            alt="Enlarged"
            onClick={(e) => {
              e.stopPropagation();
              toggleZoom();
            }}
            style={{
              maxWidth: zoomed ? '95%' : '50%',
              maxHeight: zoomed ? '95%' : '60%',
              border: '2px solid #fff',
              borderRadius: 6,
              cursor: zoomed ? 'zoom-out' : 'zoom-in',
              transition: 'all 0.3s ease',
            }}
          />
          {selectedIndex > 0 && (
            <div
              onClick={showPrev}
              style={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              &#10094;
            </div>
          )}
          {selectedIndex < structuralUrls.length - 1 && (
            <div
              onClick={showNext}
              style={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              &#10095;
            </div>
          )}
          <div
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            style={{
              position: 'absolute',
              top: 10,
              right: 20,
              color: 'white',
              fontSize: '2rem',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            &times;
          </div>
        </div>
      )}
    </div>
  );
}
